import { FFmpeg } from '@ffmpeg/ffmpeg';
import { Crop } from 'react-image-crop';

import { VideoEditSettings, VideoMetadata } from 'hooks/useFfmpeg';

import { EditableVideo } from './types';

export function getVideoEditCacheKey(editValues?: VideoEditSettings) {
  if (!editValues) return '_initial';

  // Create a filename-safe key based on edit parameters
  let key = '';

  if (editValues.flip) {
    key += `_f${editValues.flip}`;
  }

  if (editValues.mirror) {
    key += '_m';
  }

  if (editValues.crop) {
    const { x, y, width, height } = editValues.crop;
    key += `_c${x}-${y}-${width}-${height}`;
  }

  if (editValues.trim) {
    const [start, end] = editValues.trim;
    key += `_t${start}-${end}`;
  }

  if (key === '') {
    return '_initial';
  }

  return key;
}

export const toTimeString = (
  seconds: number,
  showMilliSeconds = true,
): string => {
  const sec = Math.max(0, seconds); // ensure non-negative
  const hours = Math.floor(sec / 3600);
  const minutes = Math.floor((sec - hours * 3600) / 60);
  const secs = sec - hours * 3600 - minutes * 60;

  const padded = {
    hours: hours.toString().padStart(2, '0'),
    minutes: minutes.toString().padStart(2, '0'),
    seconds: Math.floor(secs).toString().padStart(2, '0'),
  };

  const milliseconds = showMilliSeconds
    ? (secs % 1).toFixed(3).substring(1)
    : '';

  return `${padded.hours}:${padded.minutes}:${padded.seconds}${milliseconds}`;
};

export const readFileAsBase64 = async (file: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve(reader.result as string);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

export const getFileExtensions = (accept: Record<string, string[]>): string => {
  const extensions = Object.values(accept)
    .flat()
    .map((e) => e.replace('.', ''));
  if (!extensions.length) return '';

  // Remove dots and join with comma
  if (extensions.length === 1) {
    return extensions[0].replace('.', '');
  }
  return extensions.slice(0, -1).join(', ') + ' or ' + extensions.slice(-1);
};

//// FFmpeg utils

export const getThumbnails = async ({
  inputVideo,
  loadFfmpeg,
}: {
  inputVideo: EditableVideo;
  loadFfmpeg: () => Promise<FFmpeg>;
}) => {
  const ffmpeg = await loadFfmpeg();

  const inputBuffer = await inputVideo.blob.arrayBuffer();
  await ffmpeg.writeFile(inputVideo.filename, new Uint8Array(inputBuffer));

  const NUMBER_OF_IMAGES = 15;
  const fps = NUMBER_OF_IMAGES / inputVideo.metadata.duration;

  const arrayOfImageURIs = [];

  const fpsFilter = `fps=${fps}`;
  const scaleFilter = 'scale=150:-1';
  const outputPattern = 'img%d.png';

  try {
    await ffmpeg.exec([
      '-i',
      inputVideo.filename,
      '-vf',
      `${fpsFilter},${scaleFilter}`,
      '-fps_mode',
      'vfr',
      outputPattern,
    ]);

    for (let i = 0; i < NUMBER_OF_IMAGES; i++) {
      const data = await ffmpeg.readFile(`img${i + 1}.png`);

      if (typeof data !== 'string') {
        const blob = new Blob([data as BlobPart], { type: 'image/png' });
        const dataURI = await readFileAsBase64(blob);
        ffmpeg.deleteFile(`img${i + 1}.png`);
        arrayOfImageURIs.push(dataURI);
      }
    }
  } catch (error) {
    console.log({ message: error });
  } finally {
    ffmpeg.deleteFile(inputVideo.filename);
  }

  return arrayOfImageURIs;
};

export function percentageCropToAbsoluteCrop(
  percentageCrop?: Omit<Crop, 'unit'>,
  videoMetadata?: VideoMetadata,
): Crop | undefined {
  if (!percentageCrop || !videoMetadata) return undefined;

  return {
    x: (percentageCrop.x / 100) * videoMetadata.width,
    y: (percentageCrop.y / 100) * videoMetadata.height,
    width: (percentageCrop.width / 100) * videoMetadata.width,
    height: (percentageCrop.height / 100) * videoMetadata.height,
    unit: 'px',
  };
}

export function absoluteCropToPercentageCrop(
  absoluteCrop?: Omit<Crop, 'unit'>,
  videoMetadata?: VideoMetadata,
): Crop | undefined {
  if (!absoluteCrop || !videoMetadata) return undefined;

  return {
    x: (absoluteCrop.x / videoMetadata.width) * 100,
    y: (absoluteCrop.y / videoMetadata.height) * 100,
    width: (absoluteCrop.width / videoMetadata.width) * 100,
    height: (absoluteCrop.height / videoMetadata.height) * 100,
    unit: '%',
  };
}

/**
 * Rotates a crop area by 90 degrees clockwise
 * @param crop The crop area to rotate (in percentage coordinates)
 * @param isMirrored Whether the video is currently mirrored
 * @returns The rotated crop area
 */
export function rotateCropBy90Degrees(
  crop: Crop | undefined,
  isMirrored: boolean = false,
): Crop | undefined {
  if (!crop) return undefined;
  if (crop.unit !== '%') {
    console.error('rotateCropBy90Degrees - crop unit is not %');
    return crop;
  }

  let rotated: Crop;

  if (isMirrored) {
    // Different transformation when mirrored
    rotated = {
      x: crop.y,
      y: 100 - (crop.x + crop.width),
      width: crop.height,
      height: crop.width,
      unit: '%',
    };
  } else {
    // Original transformation when not mirrored
    rotated = {
      x: 100 - (crop.y + crop.height),
      y: crop.x,
      width: crop.height,
      height: crop.width,
      unit: '%',
    };
  }

  return rotated;
}

export function mirrorCrop(crop: Crop | undefined): Crop | undefined {
  if (!crop) return undefined;
  if (crop.unit !== '%') {
    console.error('mirrorCrop - crop unit is not %');
    return crop;
  }

  const mirrored: Crop = {
    x: 100 - crop.x - crop.width,
    y: crop.y,
    width: crop.width,
    height: crop.height,
    unit: '%',
  };
  return mirrored;
}

// Omit multiple keys from an object
export function omitKeys<T extends object, K extends keyof T>(
  obj: T,
  keys: readonly K[],
): Omit<T, K> {
  const result = { ...obj };
  keys.forEach((key) => delete result[key]);
  return result;
}
